import React from 'react';
import { Heart, Plus, X, Trash2 } from 'lucide-react';
import { SmallText, BodyText, Heading } from '../../../components/brand/BrandTypography';
import { PrimaryButton, SecondaryButton, TertiaryButton, IconButton } from '../../../components/brand/BrandButton';
import { BrandCard } from '../../../components/brand/BrandCard';

const RepairTab = ({ 
    repairContent, 
    repairLoading, 
    onContentChange,
    onSave,
    onAddSection,
    editingRepairSection,
    setEditingRepairSection
}) => {
    const updateRepairSection = (index, updates) => {
        const newSections = [...repairContent.sections];
        newSections[index] = { ...newSections[index], ...updates };
        onContentChange({ ...repairContent, sections: newSections });
    };

    const deleteRepairSection = (index) => {
        if (!window.confirm('Delete this section?')) return;
        const newSections = repairContent.sections.filter((_, i) => i !== index);
        onContentChange({ ...repairContent, sections: newSections });
    };

    const addQuestionToSection = (sectionIndex) => {
        const newSections = [...repairContent.sections];
        newSections[sectionIndex].questions = [
            ...(newSections[sectionIndex].questions || []),
            ''
        ];
        onContentChange({ ...repairContent, sections: newSections });
    };

    const updateQuestion = (sectionIndex, questionIndex, value) => {
        const newSections = [...repairContent.sections];
        newSections[sectionIndex].questions[questionIndex] = value;
        onContentChange({ ...repairContent, sections: newSections });
    };

    const deleteQuestion = (sectionIndex, questionIndex) => {
        const newSections = [...repairContent.sections];
        newSections[sectionIndex].questions = newSections[sectionIndex].questions.filter((_, i) => i !== questionIndex);
        onContentChange({ ...repairContent, sections: newSections });
    };

    return (
        <div className="space-y-6">
            <div className="mb-4 p-4 bg-[#FFBB98]/30 rounded-xl border border-[#FFBB98]/20">
                <SmallText className="text-[#FFBB98]">
                    <strong>Repair & Reconnection</strong> - Edit questions to help users navigate important conversations and achieve repair.
                </SmallText>
            </div>
            
            {repairLoading ? (
                <div className="text-center py-12">
                    <div className="w-3 h-3 rounded-full bg-[#FFBB98] animate-pulse mx-auto mb-4" />
                    <SmallText className="text-[#7D8E95]">Loading content...</SmallText>
                </div>
            ) : repairContent ? (
                <>
                    {/* Page Settings */}
                    <BrandCard variant="elevated" padding="lg" className="mb-6">
                        <Heading as="h3" size="text-lg" className="mb-4">Page Settings</Heading>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Title</label>
                                <input
                                    type="text"
                                    value={repairContent.title || ''}
                                    onChange={(e) => onContentChange({ ...repairContent, title: e.target.value })}
                                    className="w-full p-3 border border-[#FFBB98] rounded-lg focus:outline-none focus:border-[#FFBB98]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Subtitle</label>
                                <input
                                    type="text"
                                    value={repairContent.subtitle || ''}
                                    onChange={(e) => onContentChange({ ...repairContent, subtitle: e.target.value })}
                                    className="w-full p-3 border border-[#FFBB98] rounded-lg focus:outline-none focus:border-[#FFBB98]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Introduction</label>
                                <textarea
                                    value={repairContent.intro || ''}
                                    onChange={(e) => onContentChange({ ...repairContent, intro: e.target.value })}
                                    rows={3}
                                    className="w-full p-3 border border-[#FFBB98] rounded-lg focus:outline-none focus:border-[#FFBB98] resize-none"
                                />
                            </div>
                        </div>
                    </BrandCard>
                    
                    {/* Sections */}
                    <div className="flex items-center justify-between mb-4">
                        <Heading as="h3" size="text-lg">Question Sections</Heading>
                        <SecondaryButton onClick={onAddSection} size="sm">
                            <Plus className="w-4 h-4 mr-1" />
                            Add Section
                        </SecondaryButton>
                    </div>
                    
                    {repairContent.sections?.length === 0 ? (
                        <BrandCard variant="elevated" padding="lg" className="text-center">
                            <Heart className="w-12 h-12 text-[#FFBB98] mx-auto mb-4" />
                            <BodyText className="text-[#7D8E95] mb-4">
                                No sections yet. Add your first section to get started.
                            </BodyText>
                            <SecondaryButton onClick={onAddSection}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add First Section
                            </SecondaryButton>
                        </BrandCard>
                    ) : (
                        <div className="space-y-4">
                            {repairContent.sections.map((section, sectionIndex) => (
                                <BrandCard key={section.id || sectionIndex} variant="elevated" padding="md">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1 space-y-3">
                                            <input
                                                type="text"
                                                value={section.title}
                                                onChange={(e) => updateRepairSection(sectionIndex, { title: e.target.value })}
                                                placeholder="Section title"
                                                className="w-full p-2 border border-[#FFBB98] rounded-lg text-lg font-medium focus:outline-none focus:border-[#FFBB98]"
                                            />
                                            <textarea
                                                value={section.description || ''}
                                                onChange={(e) => updateRepairSection(sectionIndex, { description: e.target.value })}
                                                placeholder="Section description (optional)"
                                                rows={2}
                                                className="w-full p-2 border border-[#FFBB98] rounded-lg text-sm focus:outline-none focus:border-[#FFBB98] resize-none"
                                            />
                                        </div>
                                        <IconButton 
                                            onClick={() => deleteRepairSection(sectionIndex)}
                                            className="ml-2 text-red-500 hover:bg-red-50"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </IconButton>
                                    </div>
                                    
                                    {/* Questions */}
                                    <div className="space-y-2">
                                        <SmallText className="font-medium text-[#7D8E95]">Questions:</SmallText>
                                        {section.questions?.map((question, qIndex) => (
                                            <div key={qIndex} className="flex items-center gap-2">
                                                <span className="text-[#7D8E95] text-sm w-6">{qIndex + 1}.</span>
                                                <input
                                                    type="text"
                                                    value={question}
                                                    onChange={(e) => updateQuestion(sectionIndex, qIndex, e.target.value)}
                                                    placeholder="Enter question..."
                                                    className="flex-1 p-2 border border-[#FFBB98] rounded-lg text-sm focus:outline-none focus:border-[#FFBB98]"
                                                />
                                                <IconButton 
                                                    onClick={() => deleteQuestion(sectionIndex, qIndex)}
                                                    className="text-red-500 hover:bg-red-50"
                                                >
                                                    <X className="w-4 h-4" />
                                                </IconButton>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => addQuestionToSection(sectionIndex)}
                                            className="w-full p-2 border border-dashed border-[#FFBB98]/30 rounded-lg text-sm text-[#FFBB98] hover:bg-[#FFBB98]/10"
                                        >
                                            + Add Question
                                        </button>
                                    </div>
                                </BrandCard>
                            ))}
                        </div>
                    )}
                    
                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                        <PrimaryButton onClick={onSave}>
                            Save All Changes
                        </PrimaryButton>
                    </div>
                </>
            ) : (
                <div className="text-center py-12">
                    <BodyText className="text-[#7D8E95]">Failed to load content</BodyText>
                </div>
            )}
        </div>
    );
};

export default RepairTab;
